import { PageLink, PageTitle } from "_metronic/layout/core";
import { CPLAnalytics } from "setup/appInsights/CPLAnalytics";
import { useDispatch, useSelector } from "react-redux";
import { useEffect } from "react";
import { ClipLoader } from "react-spinners";
import { useThunkDispatch } from "setup/redux/thunkDispatch";
import {
  NEW_CHAT_NAME,
  selectCurrentChatName,
  selectLoadingChat,
  selectSessionStartedAt,
  startNewChat,
} from "app/modules/aiAssistant/AIAssistantSlice";
import { getChatSessions } from "app/modules/aiAssistant/AIAssistantThunks";
import useSyncChatWithRoute from "app/modules/aiAssistant/page/hooks/useSyncChatWithRoute";
import { ASSISTIMO_ROUTE } from "app/modules/aiAssistant/shared/assistimoRoutes";
import ChatsList from "app/modules/aiAssistant/page/chatsList/ChatsList";
import ChatDocumentsMenu from "app/modules/aiAssistant/page/components/ChatDocumentsMenu";
import ConversationView from "app/modules/aiAssistant/conversation/ConversationView";
import CopySupportDetailsButton from "app/modules/aiAssistant/shared/components/CopySupportDetailsButton";
import { formatSessionStarted } from "app/modules/aiAssistant/page/helpers/formatSessionStarted";
import "app/modules/aiAssistant/styles/shared.scss";
import "./AIAssistantPage.scss";

const assistantBreadCrumbs: Array<PageLink> = [
  {
    title: "Assistimo",
    path: ASSISTIMO_ROUTE,
    isSeparator: false,
    isActive: true,
  },
];

const AIAssistantPage = () => {
  const dispatch = useDispatch();
  const thunkDispatch = useThunkDispatch();
  const currentChatName = useSelector(selectCurrentChatName);
  const sessionStartedAt = useSelector(selectSessionStartedAt);
  const isLoadingChat = useSelector(selectLoadingChat);
  const startedLabel = formatSessionStarted(sessionStartedAt);

  useEffect(() => {
    thunkDispatch(getChatSessions());

    // Anything still pending is ignored once the user has left
    return () => {
      dispatch(startNewChat());
    };
  }, [dispatch, thunkDispatch]);

  useSyncChatWithRoute();

  return (
    <CPLAnalytics pageName="AIAssistant">
      <PageTitle breadcrumbs={assistantBreadCrumbs}></PageTitle>
      <div className="assistimo-page">
        <ChatsList />
        <section className="assistimo-panel assistimo-conversation" aria-label="Conversation">
          <header className="assistimo-conversation-header">
            <div className="assistimo-conversation-header__heading">
              <h1 className="assistimo-conversation-header__title" title={currentChatName ?? NEW_CHAT_NAME}>
                {currentChatName ?? NEW_CHAT_NAME}
              </h1>
              {startedLabel && <span className="assistimo-conversation-header__started">{startedLabel}</span>}
            </div>
            <div className="assistimo-conversation-header__actions">
              <ChatDocumentsMenu />
              <CopySupportDetailsButton data-test-id="assistimo-page__copy-button" />
            </div>
          </header>
          <ConversationView
            className="assistimo-chat"
            composerClassName="assistimo-readable-column assistimo-chat__composer"
            allowAttachments
            loadingView={
              isLoadingChat && (
                <div className="assistimo-chat assistimo-chat--loading">
                  <ClipLoader size={40} color="currentColor" className="assistimo-spinner" />
                  <div>Loading chat…</div>
                </div>
              )
            }
            testIds={{
              getCopyButton: (messageID) => `assistimo-page__copy-button-${messageID}`,
              getFeedback: (control, messageID) => `assistimo-page__feedback-${control}-${messageID}`,
              getSuggestedPromptButton: (promptID) => `assistimo-page__suggested-prompt-button-${promptID}`,
              thinkingToggleButton: "assistimo-page__thinking-toggle-button",
              messageInput: "assistimo-page__message-input",
              sendButton: "assistimo-page__send-message-button",
              attachButton: "assistimo-page__attach-file-button",
              fileInput: "assistimo-page__file-input",
              getRemoveDocumentButton: (documentID) => `assistimo-page__remove-document-button-${documentID}`,
            }}
          />
        </section>
      </div>
    </CPLAnalytics>
  );
};

export default AIAssistantPage;
